import { useState } from "react";
import { FileText, Receipt, Warning } from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { applyMask } from "shared/utils/masks";
import {
  DashboardBlock,
  DashboardBlockContent,
  DashboardBlockHeader,
} from "../layout/DashboardBlock";
import { BlockLoadingState } from "../states/BlockStates";
import { useContractualRent } from "./contractualRent/useContractualRent";
import { ContractualRentBreakdown } from "./contractualRent/ContractualRentBreakdown";
import { ContractualRentCompanyTable } from "./contractualRent/ContractualRentCompanyTable";
import { ContractualRentMetrics } from "./contractualRent/contractualRent.types";

const dataQualityNotes = (metrics: ContractualRentMetrics): string[] => {
  const notes: string[] = [];
  if (metrics.linesWithoutCurrentPrice > 0) {
    notes.push(
      `${metrics.linesWithoutCurrentPrice} contractlijn(en) zonder prijs die vandaag geldt — niet meegeteld.`,
    );
  }
  if (metrics.unclassifiedLineCount > 0) {
    notes.push(
      `${metrics.unclassifiedLineCount} lijn(en) met een onbekend grootboek staan onder Overige opbrengsten.`,
    );
  }
  if (metrics.quarterlyContractCount > 0) {
    notes.push(
      `${metrics.quarterlyContractCount} contract(en) worden per kwartaal gefactureerd; hun bedrag is de contractprijs zoals geregistreerd.`,
    );
  }
  return notes;
};

export const ContractualRentBlockNew = () => {
  const { metrics, isLoading, error, reload } = useContractualRent();
  const [showCompanies, setShowCompanies] = useState(false);

  const renderBody = () => {
    if (isLoading) {
      return <BlockLoadingState message="Contractuele huur aan het ophalen..." />;
    }
    if (error || !metrics) {
      return (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <p className="text-neutral-primary text-sm">
            {error ?? "Geen gegevens beschikbaar."}
          </p>
          <Button variant="outline" size="sm" onClick={reload}>
            Opnieuw proberen
          </Button>
        </div>
      );
    }

    const notes = dataQualityNotes(metrics);
    const { active, termination } = metrics.contractCountByPhase;
    const conceptCount = metrics.conceptContractCount;

    return (
      <div className="space-y-4">
        {/* Headline figures */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <div className="rounded-md border border-neutral-primary/10 bg-white p-3">
            <div className="text-neutral-secondary-mid flex items-center gap-1 text-[10px] uppercase">
              <FileText weight="duotone" size={12} />
              <span>Actieve huurcontracten</span>
            </div>
            <div className="text-foreground text-xl font-semibold tabular-nums">
              {metrics.contractCount}
            </div>
            <div className="text-neutral-secondary-mid text-[11px]">
              {active} lopend · {termination} in opzeg
            </div>
            <div className="text-neutral-primary text-[11px] font-medium">
              waarvan {conceptCount} contracten in concept
            </div>
            {metrics.attributeOnlyContractCount > 0 && (
              <div className="text-neutral-secondary-mid text-[11px]">
                waarvan {metrics.attributeOnlyContractCount} los attribuut
              </div>
            )}
          </div>
          <div className="rounded-md border border-neutral-primary/10 bg-white p-3">
            <div className="text-neutral-secondary-mid text-[10px] uppercase">
              Contractueel per maand
            </div>
            <div className="text-foreground text-xl font-semibold tabular-nums">
              {applyMask(metrics.totals.total, "valuta")}
            </div>
            <div className="text-neutral-secondary-mid text-[11px]">
              waarvan huur {applyMask(metrics.totals.rent, "valuta")}
            </div>
            <div className="text-neutral-primary text-[11px] font-medium">
              waarvan {applyMask(metrics.conceptTotal, "valuta")} uit {conceptCount}{" "}
              contracten in concept
            </div>
          </div>
          <div className="rounded-md border border-neutral-primary/10 bg-white p-3">
            <div className="text-neutral-secondary-mid text-[10px] uppercase">
              Op jaarbasis
            </div>
            <div className="text-foreground text-xl font-semibold tabular-nums">
              {applyMask(metrics.totals.total * 12, "valuta")}
            </div>
            <div className="text-neutral-secondary-mid text-[11px]">
              maandbedrag × 12
            </div>
            <div className="text-neutral-primary text-[11px] font-medium">
              waarvan {applyMask(metrics.conceptTotal * 12, "valuta")} in concept
            </div>
          </div>
        </div>

        <ContractualRentBreakdown
          categories={metrics.categories}
          total={metrics.totals.total}
        />

        <div className="border-t border-neutral-primary/10 pt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowCompanies((open) => !open)}
          >
            {showCompanies ? "Verberg per vennootschap" : "Toon per vennootschap"}
          </Button>
          {showCompanies && (
            <div className="mt-2">
              <ContractualRentCompanyTable
                companies={metrics.companies}
                categories={metrics.categories}
              />
            </div>
          )}
        </div>

        <div className="text-neutral-secondary-mid space-y-1 text-[10px] leading-relaxed">
          <p className="italic">
            Som van de prijs die vandaag geldt op elke lijn van lopende contracten,
            contracten in opzeg en contracten in concept (geïndexeerd, excl. btw). Voor
            een concept dat nog niet gestart is telt de startprijs.
          </p>
          {notes.map((note) => (
            <p key={note} className="text-neutral-primary flex items-start gap-1">
              <Warning size={12} weight="duotone" className="mt-px shrink-0" />
              {note}
            </p>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DashboardBlock>
      <DashboardBlockHeader
        icon={<Receipt weight="duotone" size={20} />}
        title="Contractuele huur"
      />
      <DashboardBlockContent padding="md">{renderBody()}</DashboardBlockContent>
    </DashboardBlock>
  );
};
